import type { NxMention } from './types';

export type NxMentionSegment =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'italic'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'break' }
  | { kind: 'mention'; mention: NxMention };

// Light markdown: `**bold**`, `*italic*` / `_italic_`, `` `code` ``, single
// newlines as <br>. Mentions are NOT parsed from the text — they ride as
// structured ranges and are spliced in before markdown so a mention spanning
// e.g. an asterisk doesn't break formatting. Renderer is pure data: callers
// turn segments into <span>/<button> in Vue (no v-html → no XSS).
export function renderMentionText(body: string, mentions: NxMention[]): NxMentionSegment[] {
  if (!body) return [];
  const sorted = [...mentions].sort((a, b) => a.start - b.start);
  const segments: NxMentionSegment[] = [];
  let cursor = 0;
  for (const m of sorted) {
    if (m.start < cursor || m.end > body.length || m.end <= m.start) continue;
    if (m.start > cursor) {
      pushFormatted(segments, body.slice(cursor, m.start));
    }
    segments.push({ kind: 'mention', mention: m });
    cursor = m.end;
  }
  if (cursor < body.length) {
    pushFormatted(segments, body.slice(cursor));
  }
  return segments;
}

function pushFormatted(out: NxMentionSegment[], text: string) {
  if (!text) return;
  // Split on newlines first so a stray break inside a `*` pair doesn't
  // swallow a paragraph.
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) out.push({ kind: 'break' });
    parseInline(out, lines[i]);
  }
}

const TOKEN_RE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`)/g;

function parseInline(out: NxMentionSegment[], line: string) {
  if (!line) return;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(line)) !== null) {
    if (match.index > lastIndex) {
      out.push({ kind: 'text', value: line.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith('**')) {
      out.push({ kind: 'bold', value: token.slice(2, -2) });
    } else if (token.startsWith('*') || token.startsWith('_')) {
      out.push({ kind: 'italic', value: token.slice(1, -1) });
    } else {
      out.push({ kind: 'code', value: token.slice(1, -1) });
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < line.length) {
    out.push({ kind: 'text', value: line.slice(lastIndex) });
  }
}
